import React, { useEffect, useRef, useCallback } from 'react';
import { Spin, Empty } from 'antd';
import { EnvironmentOutlined, CarOutlined, ToolOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
	WrenchModeEvent,
	ParsedLocationEvents,
	VisitSession,
	EVENT_LABELS,
	EMPTY_PARSED,
	formatTimer,
	parseLocationEvents,
} from '../../utils/WrenchModeEventUtils';

interface ScheduleWithEvents {
	schedule: any;
	events: WrenchModeEvent[];
	parsed: ParsedLocationEvents;
}

interface WrenchModeEventTimelineProps {
	schedulesWithEvents: ScheduleWithEvents[];
	loading: boolean;
	onVisibleDateChange: (date: string) => void;
	scrollToTargetRef: React.MutableRefObject<{ date: string; locationId: number } | null>;
	scrollContainerRef: React.RefObject<HTMLElement>;
	locationIds?: number[];
}

const formatEventTime = (createdAt: string): string => moment(createdAt).format('h:mmA');

const TIME_COL = 'bodyregular text-character-secondary';
const LABEL = 'bodyregular text-character-secondary';

const renderEventLabel = (evt: WrenchModeEvent): React.ReactNode => {
	const baseLabel = EVENT_LABELS[evt.eventType] || evt.eventType;

	if (evt.eventType === 'started_work' && evt.workOrderId) {
		return (
			<span className={LABEL}>
				{baseLabel}:{' '}
				<Link
					to={'/supplier/workOrders/detail/' + evt.workOrderId}
					className="text-character-secondary underline hover:text-character-secondary"
				>
					WO #{evt.workOrderId}
				</Link>
			</span>
		);
	}

	if (evt.eventType === 'ended_work' && evt.workOrderId) {
		const isIncomplete = evt.checkoutStatus === 'incomplete';
		return (
			<span className={LABEL}>
				{baseLabel}
				{isIncomplete ? ' \u2022 Incomplete' : ''}:{' '}
				<Link
					to={'/supplier/workOrders/detail/' + evt.workOrderId}
					className="text-character-secondary underline hover:text-character-secondary"
				>
					WO #{evt.workOrderId}
				</Link>
			</span>
		);
	}

	return <span className={LABEL}>{baseLabel}</span>;
};

const renderEventRow = (evt: WrenchModeEvent, key: React.Key): React.ReactNode => (
	<div className="flex items-start gap-2 px-m py-2" key={key}>
		<span className={TIME_COL}>{formatEventTime(evt.createdAt)}</span>
		<div className="min-w-0 flex-1">{renderEventLabel(evt)}</div>
	</div>
);

const renderBucket = (
	icon: React.ReactNode,
	label: string,
	total: string,
	events: WrenchModeEvent[]
): React.ReactNode => {
	if (events.length === 0) return null;
	return (
		<div>
			<div className="px-m py-xs">
				<div className="flex items-center gap-2 border-0 border-b border-solid border-grey-5">
					<span className="h5medium flex flex-1 items-center gap-2 text-grey-8">
						<span className="text-grey-8">{icon}</span>
						<span>{label}</span>
					</span>
					<span className="h5medium text-grey-10">{total}</span>
				</div>
			</div>
			<div className="">{events.map((evt, i) => renderEventRow(evt, i))}</div>
		</div>
	);
};

const flattenWorkEvents = (visit: VisitSession): WrenchModeEvent[] => {
	const out: WrenchModeEvent[] = [];
	for (const session of visit.workSessions) {
		for (const evt of session.events) {
			out.push(evt);
		}
	}
	return out;
};

const renderVisitSession = (
	visit: VisitSession,
	visitIndex: number,
	totalVisits: number
): React.ReactNode => {
	const visitTotalMs = visit.driveTimeMs + visit.workTimeMs;
	const workEvents = flattenWorkEvents(visit);
	return (
		<div key={visitIndex} className="flex flex-col gap-xs">
			{totalVisits > 1 ? (
				<div className="p-m">
					<div className="flex items-center justify-between gap-2 border-0 border-b border-solid border-grey-4 ">
						<span className="h5medium flex-1 text-grey-10">Session {visitIndex + 1}</span>
						<span className="h5medium text-grey-10">{formatTimer(visitTotalMs)}</span>
					</div>
				</div>
			) : null}
			{renderBucket(
				<CarOutlined />,
				'Drive Time',
				formatTimer(visit.driveTimeMs),
				visit.driveEvents
			)}
			{renderBucket(<ToolOutlined />, 'Work Time', formatTimer(visit.workTimeMs), workEvents)}
		</div>
	);
};

const filterLocationEvents = (events: WrenchModeEvent[], locationId: number): WrenchModeEvent[] => {
	const locStr = String(locationId);
	return events.filter((e) => String(e.locationId) === locStr);
};

const WrenchModeEventTimeline: React.FC<WrenchModeEventTimelineProps> = ({
	schedulesWithEvents,
	loading,
	onVisibleDateChange,
	scrollToTargetRef,
	scrollContainerRef,
	locationIds,
}) => {
	const hasLocationFilter = locationIds && locationIds.length > 0;
	const innerRef = useRef<HTMLDivElement>(null);
	const suppressObserverRef = useRef<boolean>(false);
	const suppressTimeoutRef = useRef<number | null>(null);
	const debounceTimeoutRef = useRef<number | null>(null);
	const intersectingRef = useRef<Set<HTMLElement>>(new Set());

	useEffect(() => {
		const container = scrollContainerRef.current;
		const inner = innerRef.current;
		if (!container || !inner) return;

		const intersecting = intersectingRef.current;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const el = entry.target as HTMLElement;
					if (entry.isIntersecting) {
						intersecting.add(el);
					} else {
						intersecting.delete(el);
					}
				}

				if (suppressObserverRef.current) return;

				let topEl: HTMLElement | null = null;
				let topY = Infinity;
				intersecting.forEach((el) => {
					const rect = el.getBoundingClientRect();
					if (rect.top < topY) {
						topY = rect.top;
						topEl = el;
					}
				});

				if (!topEl) return;
				const date = (topEl as HTMLElement).getAttribute('data-date');
				if (!date) return;

				if (debounceTimeoutRef.current !== null) {
					window.clearTimeout(debounceTimeoutRef.current);
				}
				debounceTimeoutRef.current = window.setTimeout(() => {
					onVisibleDateChange(date);
				}, 100);
			},
			{
				root: container,
				rootMargin: '0px 0px -80% 0px',
				threshold: 0,
			}
		);

		const sections = inner.querySelectorAll('[data-date][data-location-id]');
		sections.forEach((section) => observer.observe(section));

		return () => {
			observer.disconnect();
			intersecting.clear();
			if (debounceTimeoutRef.current !== null) {
				window.clearTimeout(debounceTimeoutRef.current);
				debounceTimeoutRef.current = null;
			}
		};
	}, [schedulesWithEvents, onVisibleDateChange, scrollContainerRef]);

	const scrollToTarget = useCallback(
		(date: string, locationId: number) => {
			const inner = innerRef.current;
			const container = scrollContainerRef.current;
			if (!inner || !container) return;
			const el = inner.querySelector(
				'[data-date="' + date + '"][data-location-id="' + locationId + '"]'
			) as HTMLElement | null;
			if (!el) return;

			// Compute target scrollTop for the container only — avoids
			// scrollIntoView's default behavior of scrolling every scrollable
			// ancestor (including the page body).
			const elRect = el.getBoundingClientRect();
			const containerRect = container.getBoundingClientRect();
			const targetScrollTop = container.scrollTop + (elRect.top - containerRect.top);

			suppressObserverRef.current = true;
			if (suppressTimeoutRef.current !== null) {
				window.clearTimeout(suppressTimeoutRef.current);
			}
			suppressTimeoutRef.current = window.setTimeout(() => {
				suppressObserverRef.current = false;
			}, 700);

			container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
		},
		[scrollContainerRef]
	);

	useEffect(() => {
		const target = scrollToTargetRef.current;
		if (target) {
			scrollToTarget(target.date, target.locationId);
			scrollToTargetRef.current = null;
		}
	});

	if (loading && schedulesWithEvents.length === 0) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spin />
			</div>
		);
	}

	if (schedulesWithEvents.length === 0) {
		return (
			<div className="bg-white px-xs">
				<Empty description="No activity logged yet" className="py-12" />
			</div>
		);
	}

	const dateGroups: Record<string, ScheduleWithEvents[]> = {};
	const dateOrder: string[] = [];
	for (const item of schedulesWithEvents) {
		const locations = (item.schedule && item.schedule.locations) || [];
		// Skip schedules with no locations — nothing to render for the day.
		if (locations.length === 0) continue;
		const date = item.schedule.date;
		if (!dateGroups[date]) {
			dateGroups[date] = [];
			dateOrder.push(date);
		}
		dateGroups[date].push(item);
	}

	return (
		<div ref={innerRef} className="bg-white">
			{dateOrder.map((date) => {
				const schedules = dateGroups[date];
				return (
					<div key={date} className="p-1">
						<div className="h4medium  bg-[#D7F0F6] p-4 text-center text-character-primary">
							{moment(date).format('MMMM D, YYYY')}
						</div>
						{schedules.map((item) => {
							const allLocations = item.schedule.locations || [];
							const locations = hasLocationFilter
								? allLocations.filter((loc: any) => locationIds.indexOf(loc.locationId) !== -1)
								: allLocations;
							return locations.map((loc: any, locIdx: number) => {
								const locationName = (loc.location && loc.location.name) || 'Unknown Location';
								const locationEvents = filterLocationEvents(item.events, loc.locationId);
								const parsed: ParsedLocationEvents =
									locationEvents.length > 0 ? parseLocationEvents(locationEvents) : EMPTY_PARSED;
								const locationTotalMs = parsed.driveTimeMs + parsed.totalWorkMs;
								const hasVisits = parsed.visitSessions.length > 0;
								const hasAnyData =
									hasVisits ||
									parsed.driveEvents.length > 0 ||
									parsed.workLogEvents.length > 0 ||
									parsed.timerEvents.length > 0;

								return (
									<div
										className="flex flex-col gap-2.5 px-3 py-4"
										key={item.schedule.id + '-' + locIdx}
										data-date={date}
										data-location-id={loc.locationId}
									>
										<div className="flex flex-row items-center rounded bg-grey-3 px-3 py-4">
											<div className="flex flex-1 flex-col gap-1">
												<div className="bodyregular flex items-center gap-1 text-grey-10">
													<EnvironmentOutlined />
													<span>{locationName}</span>
												</div>
												<div className="h5medium text-grey-10">Total Time</div>
											</div>
											<span className="h4medium text-grey-10">{formatTimer(locationTotalMs)}</span>
										</div>
										<div className="flex flex-col bg-white">
											{hasVisits
												? parsed.visitSessions.map((visit, vIdx) =>
														renderVisitSession(visit, vIdx, parsed.visitSessions.length)
												  )
												: null}
											{!hasVisits && parsed.driveEvents.length > 0
												? renderBucket(
														<CarOutlined />,
														'Drive Time',
														formatTimer(parsed.driveTimeMs),
														parsed.driveEvents
												  )
												: null}
											{!hasAnyData ? (
												<div className="bodyregular px-4 py-2 text-character-secondary">
													No activity logged yet
												</div>
											) : null}
										</div>
									</div>
								);
							});
						})}
					</div>
				);
			})}
		</div>
	);
};

export default WrenchModeEventTimeline;
